import { Injectable, Inject, DOCUMENT, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';

import { contains } from 'lib/tools';
import each from 'lodash-es/each';
import get from 'lodash-es/get';
import omit from 'lodash-es/omit';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { RouterService } from 'lib/services/router.service';

interface MetaTags {
    title?: string; // Title is technically not a meta tag, but it doesn't warrant its own interface in the current context.
    description?: string;
    keywords?: string;
    og_description?: string;
    'og:description'?: string;
    og_image?: string;
    'og:image'?: string;
    og_title?: string;
    'og:title'?: string;
    og_url?: string;
    'og:url'?: string;
    collection?: string;
}

const scriptName = 'meta.service';

const allTagNames = ['title', 'description', 'keywords', 'og:description', 'og:image', 'og:title', 'og:url'];

@Injectable()
export class MetaService {
    private appPageTitles: any;
    private dom: any;
    private environment: any;
    private meta: Meta;
    private routerService: RouterService;
    private title: Title;

    isBrowser: boolean;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject(DOCUMENT) d: any,
        @Inject('appPageTitles') a: any,
        @Inject('environment') e: any,
        m: Meta,
        r: RouterService,
        t: Title
    ) {
        try {
            this.isBrowser = isPlatformBrowser(this.platformId);
            this.appPageTitles = a;
            this.dom = d;
            this.environment = e;
            this.meta = m;
            this.routerService = r;
            this.title = t;
        } catch (err) {
            console.error(...new ExxComError(620934, scriptName, err).stamp());
        }
    }

    init() {
        try {
            this.initRouterListener();
        } catch (err) {
            console.error(...new ExxComError(820934, scriptName, err).stamp());
        }
    }

    private initRouterListener() {
        try {
            this.routerService.onRouteChange('metaService', (event: any) => {
                this.addCanonical();
                const title = this.appPageTitles[this.routerService.url.component];
                if (!title) {
                    return;
                }
                this.add({ title });
            });
        } catch (err) {
            console.error(...new ExxComError(829833, scriptName, err).stamp());
        }
    }

    private addCanonical() {
        try {
            // Skip setting canonical for 404 pages - they should never be a canonical URL.
            // Without this check, internal navigations to /404 (e.g. when a product API call
            // fails during SSR) overwrite the canonical with /404, causing Google to de-index
            // the original page under "Alternate page with proper canonical tag."
            const component = get(this.routerService, 'url.component', '');
            if (component === '404' || component === '410') {
                return;
            }

            let canonicalPath = get(this.routerService, 'url.fullPath', '');
            // Add the page param to the canonical path if present, except for page=1.
            // Page 1 is the same document as the unparameterised URL, so a self-referencing
            // ?page=1 canonical creates a duplicate instead of consolidating it. Measured:
            // 14 such duplicates indexed across EXX and SPC. (ST-3224)
            const pageParam = get(this.routerService, 'url.params.page', false);
            if (pageParam && String(pageParam) !== '1') {
                canonicalPath += `?page=${pageParam}`;
            }
            const prevTag = this.dom.head.querySelector('link[rel="canonical"]');
            if (prevTag) {
                prevTag.setAttribute('href', canonicalPath);
            } else {
                const tag = this.dom.createElement('link');
                tag.setAttribute('href', canonicalPath);
                tag.setAttribute('rel', 'canonical');
                this.dom.head.appendChild(tag);
            }
        } catch (err) {
            console.error(...new ExxComError(2094881, scriptName, err).stamp());
        }
    }

    add(data: MetaTags = {}) {
        try {
            // Parse and get names from data

            const metas: MetaTags = {};
            const names: string[] = Object.keys(data).map((n: string) => {
                const name: string = n.replace('_', ':');
                metas[name] = data[n];
                return name;
            });

            // Remove previous

            allTagNames.forEach((tagName: string) => {
                if (!contains(names, tagName) || !metas[tagName]) {
                    this.meta.removeTag(`name='${tagName}'`);
                }
            });

            // Title tag

            const newTitle: string = get(metas, 'title');
            newTitle && this.title.setTitle(newTitle);

            // Non-title meta tags

            const newMetas: MetaTags = omit(metas, 'title');

            // init collections default
            let defaultCollection = '';
            if (!newMetas['collection']) {
                const s = get(this.routerService, 'url.parts', []).join(' ').split('-');
                defaultCollection = s.join(' ');
            }
            // Defaults

            if (!newMetas['description']) {
                this.meta.updateTag({
                    name: 'description',
                    content: this.environment.siteName,
                });
            }
            if (!newMetas['og:description']) {
                this.meta.updateTag({
                    name: 'og:description',
                    content: newMetas['description'] || this.environment.siteName,
                });
            }
            if (!newMetas['og:image']) {
                this.meta.updateTag({
                    name: 'og:image',
                    content: this.environment.urls.image.logo,
                });
            }
            if (!newMetas['og:url']) {
                this.meta.updateTag({
                    name: 'og:url',
                    content: this.routerService.url.full,
                });
            }
            if (!newMetas['title']) {
                this.meta.updateTag({ name: 'title', content: this.title.getTitle() });
            }
            if (!newMetas['og:title']) {
                this.meta.updateTag({ name: 'og:title', content: this.title.getTitle() });
            }
            if (!newMetas['collection']) {
                this.meta.updateTag({
                    name: 'collection',
                    content: defaultCollection,
                });
            }

            // Add or update
            each(newMetas, (content: string, name: string) => {
                const definition: MetaDefinition = { name, content };
                if (name == 'description') {
                    const site = this.environment.siteName == 'Exxact' ? '@Exxactcorp' : '@sabrePC';
                    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
                    this.meta.updateTag({ name: 'twitter:site', content: site });
                }
                if (name == 'og:image') {
                    this.meta.updateTag({ name: 'twitter:image', content: content });
                }
                if (content) {
                    this.meta.updateTag(definition);
                }
            });
        } catch (err) {
            console.error(...new ExxComError(832203, scriptName, err).stamp());
        }
    }

    // takes in product information and adds the product information to the head of the document as meta info
    addProductMetadata(product: any) {
        try {
            this.addProductStructuredData(product);
            this.meta.updateTag({ name: 'sku', content: product && product.name });
            this.meta.updateTag({
                name: 'title',
                content: `${product.description} | ${this.environment.siteName}`,
            });
            this.meta.updateTag({
                name: 'productID',
                content: product && `mpn:${product.mpn}`,
            });
            this.meta.updateTag({
                name: 'url',
                content: this.routerService.url.base + `${product.urlComponent}`,
            });
            this.meta.updateTag({
                name: 'brand',
                content: product && product.manufacturer,
            });
            this.meta.updateTag({
                name: 'description',
                content: product && product.description,
            });
            this.meta.updateTag({
                name: 'image',
                content: product && product.imageUrl,
            });
            this.meta.updateTag({
                name: 'mpn',
                content: product && `mpn:${product.mpn}`,
            });
            this.meta.updateTag({ name: 'name', content: product && product.name });
            this.meta.updateTag({
                name: 'availability',
                content: product && (product.inventory > 0).toString(),
            });
            this.meta.updateTag({
                name: 'price',
                content: product && product.price,
            });
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            this.meta.updateTag({
                name: 'priceValidUntil',
                content: tomorrow.toString(),
            });
        } catch (err) {
            console.error(...new ExxComError(837203, scriptName, err).stamp());
        }
    }

    addProductStructuredData(product: any = {}) {
        try {
            if (!this.isBrowser) {
                return;
            }
            const el = document.getElementById('product-structured-data');
            if (el) {
                return;
            }

            const {
                name = '',
                mpn = '',
                manufacturer,
                description = '',
                imageUrl = '',
                price = 0,
                sku = '',
                inventory = 0,
                urlComponent = '',
            } = product;

            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);

            const escapedDescription = JSON.stringify(description);

            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.id = 'product-structured-data';
            script.innerHTML = `{
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": "${name}",
        "description": ${escapedDescription},
        "image": "${imageUrl}",
        "brand": "${manufacturer}",
        "sku": "${sku}",
        "mpn": "${mpn}",
        "offers": {
          "@type": "Offer",
          "price": "${price}",
          "priceValidUntil" : "${tomorrow}",
          "priceCurrency": "USD",
          "availability": "${inventory > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'}",
          "url": "${urlComponent}"
        }
      }`;

            document.head.appendChild(script);
        } catch (err) {
            console.error(...new ExxComError(837293, scriptName, err).stamp());
        }
    }

    removeProductMetadata() {
        try {
            this.meta.removeTag("name='sku'");
            this.meta.removeTag("name='productID'");
            this.meta.removeTag("name='url'");
            this.meta.removeTag("name='brand'");
            this.meta.removeTag("name='description'");
            this.meta.removeTag("name='image'");
            this.meta.removeTag("name='mpn'");
            this.meta.removeTag("name='name'");
            this.meta.removeTag("name='availability'");
            this.meta.removeTag("name='priceValidUntil'");
            if (!this.isBrowser) {
                return;
            }
            const el = document.getElementById('product-structured-data');
            if (!el) {
                return;
            }
            el.remove();
        } catch (err) {
            console.error(...new ExxComError(937243, scriptName, err).stamp());
        }
    }
}
